import { useEffect, useState } from 'react';
import { FileText, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import PrdDocumentPreview from '@/components/PrdDocumentPreview';
import { getConfirmDocLabels } from '@/lib/confirmDocLabels';
import { cn } from '@/lib/utils';

/**
 * Opens confirm document preview in a scrollable modal.
 * @param {'link' | 'button'} triggerVariant
 */
export default function PrdDocumentModal({
  sessionId,
  className,
  triggerVariant = 'link',
  modalDescription,
  labels: labelsProp,
  intent,
  confirmDocType,
}) {
  const [open, setOpen] = useState(false);
  const labels = labelsProp || getConfirmDocLabels({ intent, confirmDocType });

  useEffect(() => {
    setOpen(false);
  }, [sessionId]);

  if (!sessionId) {
    return null;
  }

  const description = modalDescription ?? labels.modalDescription;

  const openModal = () => setOpen(true);

  return (
    <>
      {triggerVariant === 'button' ? (
        <Button
          type="button"
          onClick={openModal}
          className={cn(
            'w-full bg-gradient-to-r from-cp-mod to-cp-imp text-white shadow-md hover:opacity-95 hover:opacity-95',
            className
          )}
        >
          <FileText className="mr-2 h-4 w-4" />
          {labels.viewButton}
        </Button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          className={cn(
            'inline-flex items-center gap-1.5 text-sm font-medium text-cp-mod underline-offset-4 hover:text-cp-fg hover:underline',
            className
          )}
        >
          <FileText className="h-4 w-4 shrink-0" />
          {labels.viewButton}
          <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[min(92vh,900px)] w-[min(96vw,56rem)] max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-cp-line bg-gradient-to-r from-cp-inset to-cp-surface px-6 py-4 text-left">
            <DialogTitle className="flex items-center gap-2 text-cp-fg">
              <FileText className="h-5 w-5" />
              {labels.modalTitle}
            </DialogTitle>
            <DialogDescription className="text-cp-mod/80">{description}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <PrdDocumentPreview key={sessionId} sessionId={sessionId} active={open} labels={labels} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
